import React, { useMemo, useState } from "react";
import { Instagram, Download, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toPng } from "html-to-image";
import { getInstagramStudioPosts, THEME_KEYS } from "../data/instagramStudioPosts";

const ALL = getInstagramStudioPosts();
const PER_PAGE = 40;

const THEME_FILTERS = [{ key: "all", label: "All themes" }, ...THEME_KEYS.map((t) => ({ key: t, label: t }))];
const zipSlug = (t) => t.toLowerCase().replace(/&/g, "and").replace(/\s+/g, "-");
const FORMAT_FILTERS = [
  { key: "all", label: "All formats" },
  { key: "list", label: "Numbered" },
  { key: "stat", label: "Stats" },
  { key: "principle", label: "Principles" },
];

const Frame = ({ children, id }) => (
  <div
    id={id}
    className="relative aspect-[4/5] rounded-xl overflow-hidden bg-[#0a2540] border border-white/10 p-6 flex flex-col"
    style={{ backgroundImage: "radial-gradient(120% 90% at 100% 0%, #16355c 0%, #0a2540 55%)" }}
  >
    <div className="flex items-center justify-between mb-5">
      <span className="font-display font-800 text-white tracking-tight text-lg">BPC</span>
      <Instagram className="w-4 h-4 text-slate-300" />
    </div>
    {children}
    <div className="mt-auto pt-4 text-[11px] text-slate-400">b-p-c.net</div>
  </div>
);

const Tag = ({ children }) => (
  <span className="inline-block self-start rounded-full border border-[#5eb0f5]/50 text-[#9ecbf3] text-[10px] font-700 uppercase tracking-widest px-3 py-1 mb-4">
    {children}
  </span>
);

const PostCard = ({ post }) => {
  if (post.type === "list") {
    return (
      <Frame id={post.id}>
        <Tag>{post.tag}</Tag>
        <h3 className="font-display font-800 text-white text-[19px] leading-tight">{post.title}</h3>
        <p className="mt-2 text-[12px] text-slate-300 leading-snug">{post.sub}</p>
        <div className="mt-4 space-y-2">
          {post.points.map((pt, i) => (
            <div key={i} className="flex items-start gap-3 rounded-lg bg-white/5 border border-white/10 p-2.5">
              <span className="flex items-center justify-center w-6 h-6 shrink-0 rounded-full border border-[#5eb0f5] text-[#9ecbf3] text-[11px] font-700">{i + 1}</span>
              <span className="text-[12px] text-slate-100 leading-snug">{pt}</span>
            </div>
          ))}
        </div>
      </Frame>
    );
  }
  if (post.type === "stat") {
    return (
      <Frame id={post.id}>
        <Tag>{post.tag}</Tag>
        <div className="flex-1 flex flex-col justify-center">
          <div className="font-display font-900 text-white text-6xl leading-none">{post.stat}</div>
          <p className="mt-4 text-[14px] text-slate-200 leading-snug">{post.line}</p>
        </div>
      </Frame>
    );
  }
  return (
    <Frame id={post.id}>
      <Tag>{post.tag}</Tag>
      <div className="flex-1 flex items-center">
        <p className="font-display font-800 text-white text-[22px] leading-tight">
          <span className="text-[#5eb0f5]">“</span>{post.text}<span className="text-[#5eb0f5]">”</span>
        </p>
      </div>
    </Frame>
  );
};

const Studio = () => {
  const [theme, setTheme] = useState("all");
  const [format, setFormat] = useState("all");
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    return ALL.filter(
      (p) => (theme === "all" || p.theme === theme) && (format === "all" || p.type === format)
    );
  }, [theme, format]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const current = Math.min(page, totalPages);
  const pagePosts = filtered.slice((current - 1) * PER_PAGE, current * PER_PAGE);

  const resetTo = (setter) => (val) => {
    setter(val);
    setPage(1);
  };

  const downloadOne = async (id) => {
    setBusy(true);
    try {
      const node = document.getElementById(id);
      if (node) {
        const dataUrl = await toPng(node, { pixelRatio: 3, cacheBust: true, backgroundColor: "#0a2540" });
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `bpc-instagram-${id}.png`;
        a.click();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <section className="bg-[#0a2540] text-white py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <span className="inline-flex items-center gap-2 text-sm font-700 uppercase tracking-widest text-[#5eb0f5]">
            <Instagram className="w-5 h-5" /> Content Studio
          </span>
          <h1 className="font-display font-800 text-4xl md:text-5xl mt-3 max-w-3xl leading-[1.05]">
            500 Instagram posts across 5 growth themes
          </h1>
          <p className="mt-4 text-slate-300 text-lg max-w-2xl">
            Ready-to-share BPC designs in navy, silver and white — spanning sustainable growth, AI &amp; execution,
            innovation &amp; transformation, people &amp; culture, and customer experience. Download the full set or
            grab any tile as a high-res PNG.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href="/bpc-instagram-500-posts.zip"
              download
              data-testid="download-all-zip"
              className="inline-flex items-center gap-2 bg-white text-[#0a2540] hover:bg-slate-100 font-600 px-6 py-3 rounded-full transition-colors"
            >
              <Download className="w-4 h-4" /> Download all 500 (ZIP)
            </a>
          </div>
          <div className="mt-4">
            <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">Download by theme (100 each)</p>
            <div className="flex flex-wrap gap-2">
              {THEME_KEYS.map((t) => (
                <a
                  key={t}
                  href={`/bpc-instagram-${zipSlug(t)}.zip`}
                  download
                  data-testid={`download-theme-${zipSlug(t)}`}
                  className="inline-flex items-center gap-2 border border-white/25 hover:bg-white/10 text-white text-sm font-600 px-4 py-2 rounded-full transition-colors"
                >
                  <Download className="w-3.5 h-3.5" /> {t}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-8 py-12">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {THEME_FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`theme-filter-${f.key}`}
              onClick={() => resetTo(setTheme)(f.key)}
              className={`px-4 py-2 rounded-full text-sm font-600 transition-colors ${
                theme === f.key ? "bg-[#0a2540] text-white" : "bg-slate-100 text-[#0a2540] hover:bg-slate-200"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-8">
          {FORMAT_FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`format-filter-${f.key}`}
              onClick={() => resetTo(setFormat)(f.key)}
              className={`px-4 py-1.5 rounded-full text-xs font-600 transition-colors ${
                format === f.key ? "bg-[#5eb0f5] text-[#0a2540]" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          ))}
          <span className="ml-auto text-sm text-slate-500" data-testid="post-count">{filtered.length} posts</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="post-grid">
          {pagePosts.map((post) => (
            <div key={post.id} className="group">
              <div className="relative">
                <PostCard post={post} />
                <div className="absolute inset-0 rounded-xl bg-black/0 group-hover:bg-black/10 transition-colors pointer-events-none" />
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
                <span>Post {post.num} / 500</span>
                <button
                  onClick={() => downloadOne(post.id)}
                  disabled={busy}
                  className="inline-flex items-center gap-1 font-600 text-[#0a6ed1] hover:text-[#0a2540] transition-colors disabled:opacity-60"
                >
                  <Download className="w-3.5 h-3.5" /> PNG
                </button>
              </div>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="mt-12 flex items-center justify-center gap-4" data-testid="pagination">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={current === 1}
              data-testid="page-prev"
              className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-slate-200 text-sm font-600 text-[#0a2540] hover:bg-slate-50 disabled:opacity-40"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <span className="text-sm text-slate-500" data-testid="page-indicator">
              Page {current} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={current === totalPages}
              data-testid="page-next"
              className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-slate-200 text-sm font-600 text-[#0a2540] hover:bg-slate-50 disabled:opacity-40"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default Studio;
